import React, { useState, useMemo, useCallback } from "react";
import { Box, Button, Grid2, Typography } from "@mui/material";
import BreakdownChartBase from "./BreakdownChartBase";
import ChartDrillDownModalV2 from "./ChartDrillDown/ChartDrillDownModalV2";
import { CHART_CONFIG, CHART_TYPES } from "./chartConfig";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import { useChartDrillDown } from "./hooks/useChartDrillDown";
import SecondsFormatter, { SecondsFormatterDetailed } from "../../../Helper/Formatters/SecondsFormatter";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import { useBmsMetaInfoFields, useBmsTableConfig } from "./useBmsTableConfig";

// Transform API response to chart data format
const transformChartData = (responseData) => {
  const transformedChartData = responseData.map(item => {
    const chartItem = {
      alertName: item.xLabel,
      autoResolved: 0,
      manualResolved: 0,
    };

    item.metricsArray.forEach(metric => {
      if (metric.type === "AutoResolvedAverage") {
        chartItem.autoResolved = metric.value;
      } else if (metric.type === "ManualResolvedAverage") {
        chartItem.manualResolved = metric.value;
      }
    });

    return chartItem;
  });

  // Sort by total resolution time (autoResolved + manualResolved) in descending order
  return transformedChartData.sort((a, b) => {
    const totalA = (a.autoResolved || 0) + (a.manualResolved || 0);
    const totalB = (b.autoResolved || 0) + (b.manualResolved || 0);
    return totalB - totalA; // Descending order
  });
};

// Prepare chart options and series for ApexCharts
const getChartConfig = (chartData, additionalState) => {
  const { resolutionType: resType } = additionalState || { resolutionType: "auto" };

  if (!chartData || chartData.length === 0) {
    return { series: [], options: {} };
  }

  // Filter chart data to only show items that have values for the selected resolution type
  let filteredChartData = chartData.filter(item => {
    if (resType === "auto") {
      return item.autoResolved > 0;
    } else {
      return item.manualResolved > 0;
    }
  });

  // If no data after filtering, return empty config
  if (filteredChartData.length === 0) {
    return { series: [], options: {} };
  }

  // Sort by the displayed value (bar length) in descending order
  filteredChartData = filteredChartData.sort((a, b) => {
    const valueA = resType === "auto" ? (a.autoResolved || 0) : (a.manualResolved || 0);
    const valueB = resType === "auto" ? (b.autoResolved || 0) : (b.manualResolved || 0);
    return valueB - valueA; // Descending order
  });

  const categories = filteredChartData.map(item => item.alertName);


  // Show only the selected resolution type data (raw values)
  const rawData = resType === "auto"
    ? filteredChartData.map(item => item.autoResolved || 0)
    : filteredChartData.map(item => item.manualResolved || 0);

  // Calculate max value from raw data
  const maxValue = Math.max(...rawData);

  // Visual series data with minimum bar width for clickability
  // Very small values get lifted to a small baseline, but labels still show raw values
  const NO_VALUE_MIN_WIDTH = maxValue > 0 ? maxValue * 0.02 : 1;  // ~2% of max (or 1 if all zero)
  const MIN_WIDTH = maxValue > 0 ? maxValue * 0.03 : 1;          // ~6% of max as reasonable min length

  const seriesData = rawData.map((value) => {
    if (!value) return NO_VALUE_MIN_WIDTH;
    if (value >= MIN_WIDTH) return value;
    return MIN_WIDTH;
  });

  // Estimate label width: longest formatted label (based on raw values)
  const formattedLabels = rawData.map(val => SecondsFormatterDetailed(val, 'short'));
  const longestLabelLength = Math.max(...formattedLabels.map(label => label.length));

  // Dynamic buffer based on label length (each char ~8px, convert to data space)
  // For safety, use generous buffer: 30-40%
  const bufferPercentage = Math.max(0.30, longestLabelLength * 0.04);
  const visualMax = Math.max(...seriesData);
  const xAxisMax = visualMax * (1 + bufferPercentage);

  const series = [
    {
      name: resType === "auto" ? "Auto Resolved" : "Manually Resolved",
      data: seriesData,
    },
  ];

  // Only return overrides - common config is handled by base component
  const options = {
    chart: {
      stacked: false,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        dataLabels: {
          position: 'top', // Position at the end of horizontal bars
        }
      },
    },
    colors: ["#0B84FE"], // Single blue color for the bars
    dataLabels: {
      enabled: true,
      formatter: function (value, opts) {
        const dataPointIndex = opts?.dataPointIndex ?? 0;
        const rawValue = rawData[dataPointIndex] ?? value;
        return SecondsFormatterDetailed(rawValue, 'short');
      },
      style: {
        fontSize: "12px",
        fontWeight: 400,
        colors: ["#494949"],
      },
      offsetX: 10, // Base offset from bar end
      textAnchor: "start", // Anchor at start (right side of bar for horizontal)
    },
    xaxis: {
      categories: categories,
      max: xAxisMax, // Set maximum to ensure space for data labels
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return SecondsFormatterDetailed(val, 'short');
        },
      },
    },
  };

  return { series, options };
};

const ResolutionDurationBreakdownChart = ({ startDate, endDate, hierarchicalLocation, alertCategories, operationalStatus, globalFilters }) => {
  const { baseEntityType, baseEntityTypeIDLable, baseEntityTypeLable, baseEntityTypePluralLable } = useBaseEntityInfo();
  const [resolutionType, setResolutionType] = useState("auto"); // "auto" or "manual"
  const [groupBy, setGroupBy] = useState("originCity");

  // Configure drill-down hook with chart-specific overrides
  // Columns come from the bmsAlertV2 org meta when it configures them, and from
  // chartConfig otherwise, so an unconfigured org is unaffected.
  const resolveTable = useBmsTableConfig();
  const resolveMetaInfo = useBmsMetaInfoFields();

  const drillDownConfig = useMemo(() => ({
    chartType: CHART_TYPES.RESOLUTION_DURATION_BREAKDOWN,
    csvFileNamePrefix: "BmsFaults_ResolutionDurationBreakdown",

    // Level 1: Custom groupBy
    getLevel1GroupBy: () => [groupBy],

    // Level 1: Custom filters
    getLevel1Filters: (dataPoint, baseFilters) => {
      const resolutionTypeValue = dataPoint.resolutionType === "Auto Resolved" ? "AutoResolved" : "ManualResolved";
      const groupByValue = dataPoint?.selectedItem?.label;
      let filters = [];
      filters.push({ name: "errorDescription", values: [dataPoint.faultName] });
      filters.push({ name: "ResolutionType", values: [resolutionTypeValue] });
      if (groupByValue?.toLowerCase() !== 'all') {
        filters.push({ name: groupBy, values: [groupByValue] });
      }
      return [...baseFilters, ...filters];
    },

    // Level 1: Custom select and metaInfo
    getLevel1Select: () => ["ResolvedFaultCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration"],
    getLevel1MetaInfo: () => resolveMetaInfo([], "resolutionDurationBreakdownSummaryTable"),

    // Level 1: Custom table config
    getTableConfig: (level) => {
      const resolutionDurationConfig = CHART_CONFIG[CHART_TYPES.RESOLUTION_DURATION_BREAKDOWN];
      if (level === 1) {
        const summaryTable = resolutionDurationConfig?.popupConfig?.summaryTable;
        if (!summaryTable) return null;
        const resolved = resolveTable("resolutionDurationBreakdownSummaryTable", summaryTable);
        return {
          title: (selectedDataPoint) => selectedDataPoint?.faultName || "",
          tableColumnsConfig: resolved.tableColumnsConfig,
          tableColumnOrder: resolved.tableColumnOrder,
          emptyMessage: "No alerts found for the selected criteria",
        };
      }
      if (level === 2) {
        const vehiclesTable = resolutionDurationConfig?.popupConfig?.vehiclesTable;
        if (!vehiclesTable) return null;
        const resolved = resolveTable("resolutionDurationBreakdownVehiclesTable", vehiclesTable);
        return {
          title: vehiclesTable.title,
          tableColumnsConfig: resolved.tableColumnsConfig,
          tableColumnOrder: resolved.tableColumnOrder,
          emptyMessage: `No ${baseEntityTypePluralLable.toLowerCase()} found for the selected fault`,
        };
      }
      return null;
    },

    // Level 1: Custom modal title
    getModalTitle: (dataPoint) => {
      const faultName = dataPoint?.faultName || "";
      const category = formatDateRangeWithSmartYear(startDate, endDate);
      const firstGroupBy = dataPoint?.groupBy?.label || "By City";
      const secondGroupBy = dataPoint?.selectedItem?.label || "";
      const resolutionTypeLabel = dataPoint?.resolutionType || "Resolved";
      return (
        <Grid2 container gap={1} alignItems={"center"}>
          <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
            {`Battery System Fault Avg. Resolution Duration Breakdown: ${firstGroupBy} / ${secondGroupBy} / ${resolutionTypeLabel}`}
            <Typography variant="fw500s16c000000" sx={{ lineHeight: "28px" }}>
              {' / '}{faultName}
            </Typography>
          </Typography>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
            {'•'}
          </Typography>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>
            {category}
          </Typography>
        </Grid2>
      );
    },

    // Level 1: Custom CSV filename
    getCSVFileName: (dataPoint) => {
      const faultName = dataPoint?.faultName || "Faults";
      const resolutionTypeLabel = dataPoint?.resolutionType || "Resolved";
      const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return `BmsFaults_ResolutionDurationBreakdown_${faultName}_${resolutionTypeLabel}_${formattedStartDate}-${formattedEndDate}.csv`;
    },

    // Level 2: Custom filters
    getLevel2Filters: (level2Record, dataPoint, baseFilters) => {
      const resolutionTypeValue = dataPoint.resolutionType === "Auto Resolved" ? "AutoResolved" : "ManualResolved";
      const groupByValue = level2Record?.xLabel;
      return [
        ...baseFilters,
        { name: groupBy, values: [groupByValue] },
        { name: "errorDescription", values: [dataPoint.faultName] },
        { name: "ResolutionType", values: [resolutionTypeValue] },
      ];
    },

    // Level 2: Custom select and metaInfo
    getLevel2Select: () => ["ResolvedFaultCount"],
    getLevel2MetaInfo: () => resolveMetaInfo(["licensePlateNumber", "originCountry", "originState", "originCity", "originDepo", "pingStatus"], "resolutionDurationBreakdownVehiclesTable"),
    getLevel2GroupBy: () => ["batteryID"],

    // Level 2: Custom CSV filename
    level2GetCSVFileName: (level2Record, dataPoint) => {
      const groupByValue = level2Record?.xLabel || level2Record?.faultName || "Fault";
      const resolutionTypeLabel = dataPoint?.resolutionType || "Resolved";
      const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      return `BmsFaults_${baseEntityTypePluralLable}_${groupByValue}_${resolutionTypeLabel}_${formattedStartDate}-${formattedEndDate}.csv`;
    },
  }), [startDate, endDate, groupBy, baseEntityTypePluralLable, resolveTable, resolveMetaInfo]);

  // Use the drill-down hook
  const drillDown = useChartDrillDown({
    startDate,
    endDate,
    hierarchicalLocation,
    // The customer scope lives on the globalFilters object this chart already
    // receives, the same object its own chart request is scoped by.
    fleetIds: globalFilters?.fleets,
    // The depot half of the same scope, from the same object.
    onboardingLocations: globalFilters?.onboardingLocations,
    allOnboardingLocations: globalFilters?.allOnboardingLocations,
    alertCategories,
    operationalStatus,
    filters: [],
    config: drillDownConfig,
  });

  // Handle data point click from BreakdownChartBase
  const handleDataPointClick = (clickedData, currentGroupBy) => {
    setGroupBy(currentGroupBy);
    drillDown.handleDataPointClick(clickedData);
  };


  // Render toggle buttons for resolution type
  const renderChartControls = useCallback(() => (
    <Box>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-start", mb: 2, flexShrink: 0 }}>
        <Button
          variant={resolutionType === "auto" ? "contained" : "outlined"}
          onClick={() => setResolutionType("auto")}
          sx={{
            fontSize: "12px",
            lineHeight: '16px',
            fontWeight: resolutionType === "auto" ? 500 : 400,
            textTransform: "none",
            borderRadius: "20px",
            padding: "6px 14px",
            backgroundColor: resolutionType === "auto" ? "#F7F7FF" : "transparent",
            color: resolutionType === "auto" ? "#6D72F6" : "#494949",
            border: resolutionType === "auto" ? "1px solid #6D72F6" : "1px solid #E6E6E6",
            boxShadow: 'none',
            "&:hover": {
              backgroundColor: resolutionType === "auto" ? "#F7F7FF" : "#F8F8F8",
              border: resolutionType === "auto" ? "1px solid #6D72F6" : "1px solid #E6E6E6",
              boxShadow: 'none',
            },
          }}
        >
          Auto Resolved
        </Button>
        <Button
          variant={resolutionType === "manual" ? "contained" : "outlined"}
          onClick={() => setResolutionType("manual")}
          sx={{
            fontSize: "12px",
            lineHeight: '16px',
            fontWeight: resolutionType === "manual" ? 500 : 400,
            textTransform: "none",
            borderRadius: "20px",
            padding: "6px 14px",
            backgroundColor: resolutionType === "manual" ? "#F7F7FF" : "transparent",
            color: resolutionType === "manual" ? "#6D72F6" : "#494949",
            border: resolutionType === "manual" ? "1px solid #6D72F6" : "1px solid #E6E6E6",
            boxShadow: 'none',
            "&:hover": {
              backgroundColor: resolutionType === "manual" ? "#F7F7FF" : "#F8F8F8",
              border: resolutionType === "manual" ? "1px solid #6D72F6" : "1px solid #E6E6E6",
              boxShadow: 'none',
            },
          }}
        >
          Manually Resolved
        </Button>
      </Box>
      <Typography variant="fw600s12c8D8D8D" sx={{ textTransform: 'uppercase', color: '#8F8F8F', mb: 1 }}>
        Fault Name
      </Typography>
    </Box>
  ), [resolutionType]);

  const getAdditionalState = useCallback(() => ({ resolutionType }), [resolutionType]);

  const emptyStateMessage = useCallback((data) => {
    if (data.length === 0) {
      return "No chart data available";
    }
    return `No data available for ${resolutionType === "auto" ? "Auto Resolved" : "Manually Resolved"} faults`;
  }, [resolutionType]);

  const config = useMemo(() => ({
    sidebarSelect: "ResolvedFaultCount",
    chartSelect: "AverageResolutionTime",
    chartGroupBy: ["errorDescription", "ResolutionType"],
    transformChartData,
    getChartConfig,
    getAdditionalState,
    renderChartControls,
    chartPadding: "12px 28px 24px",
    emptyStateMessage,
    xAxisTickFormatter: (value) => SecondsFormatter(value, 'short'),
  }), [getAdditionalState, renderChartControls, emptyStateMessage]);

  return (
    <>
      <BreakdownChartBase
        title="Battery System Fault Avg. Resolution Duration Breakdown"
        height="600px"
        minVisibilityHeight={360}
        startDate={startDate}
        endDate={endDate}
        hierarchicalLocation={hierarchicalLocation}
        alertCategories={alertCategories}
        globalFilters={globalFilters}
        config={config}
        chartType="resolutionDurationBreakdown"
        onDataPointClick={handleDataPointClick}
      />
      {drillDown.getTableConfig(1) && (
        <ChartDrillDownModalV2
          open={drillDown.modalOpen}
          onClose={drillDown.handleModalClose}
          title={drillDown.getModalTitle}
          selectedDataPoint={drillDown.selectedDataPoint}
          tableConfigs={drillDown.getTableConfig(1)}
          getTableData={drillDown.getTableData}
          applyTableAction={drillDown.applyTableAction}
          getCSVFileName={drillDown.getCSVFileName}
          level2TableConfigs={drillDown.getTableConfig(2)}
          level2GetTableData={drillDown.level2GetTableData}
          level2ApplyTableAction={drillDown.level2ApplyTableAction}
          level2GetCSVFileName={drillDown.level2GetCSVFileName}
          level2Title={drillDown.level2Title}
        />
      )}
    </>
  );
};

export default ResolutionDurationBreakdownChart;
